import { useState, useMemo } from 'react'
import Fuse from 'fuse.js'
import { buildSearchIndex, type SearchableItem } from '@/lib/search-index'

export type SortOption = 'relevance' | 'title' | 'category'
export type CategoryFilter = 'all' | 'blog' | 'project' | 'service' | 'page'

/**
 * Multiplier applied to a core page's Fuse score before ranking. Below 1, so a
 * page outranks a blog post of comparable match quality. Deliberately a boost
 * rather than a hard pin: a genuinely stronger post match still wins.
 */
const PAGE_SCORE_BOOST = 0.5

export function useSearch() {
  const [query, setQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [sortBy, setSortBy] = useState<SortOption>('relevance')

  // Build search index
  const searchIndex = useMemo(() => buildSearchIndex(), [])

  // Configure Fuse.js
  const fuse = useMemo(
    () =>
      new Fuse(searchIndex, {
        keys: [
          { name: 'title', weight: 2 },
          { name: 'tags', weight: 1.5 },
          { name: 'description', weight: 1 },
          { name: 'category', weight: 1.5 },
        ],
        threshold: 0.4,
        includeScore: true,
      }),
    [searchIndex]
  )

  // Perform search with category filtering and sorting
  const allResults = useMemo(() => {
    if (!query.trim()) return []

    const trimmedQuery = query.trim().toLowerCase()

    // Special handling for category-based searches
    const categoryMap: Record<string, 'blog' | 'project' | 'service'> = {
      blog: 'blog',
      blogs: 'blog',
      'blog posts': 'blog',
      'blog post': 'blog',
      project: 'project',
      projects: 'project',
      service: 'service',
      services: 'service',
    }

    let results: SearchableItem[] = []

    // If query matches a category, filter by category first
    const matchedCategory = categoryMap[trimmedQuery]
    if (matchedCategory) {
      results = searchIndex.filter(item => item.category === matchedCategory)
    } else {
      // Otherwise, use Fuse.js for fuzzy search.
      //
      // Core pages are boosted ahead of content that merely mentions the same
      // word. Someone typing "pricing" wants /pricing, not the five articles
      // discussing pricing; without this the destination loses to the corpus
      // simply because 97 posts have more surface area than one page. Fuse
      // scores 0 as a perfect match and 1 as the worst, so scaling a page's
      // score down moves it up the list. The boost only reorders what Fuse
      // already matched — it never injects a page that did not match.
      const searchResults = fuse.search(query)
      results = [...searchResults]
        .sort((a, b) => {
          // A result with no score is treated as the worst match AND is not
          // boosted. Applying the page boost to a missing score produced
          // 1 * 0.5 = 0.5, which outranks a genuinely good 0.9 match, so an
          // absent score could quietly promote a page above better hits
          // (Qodo round 1, finding 5).
          const weight = (r: (typeof searchResults)[number]) => {
            if (r.score == null) return 1
            return r.score * (r.item.category === 'page' ? PAGE_SCORE_BOOST : 1)
          }
          const delta = weight(a) - weight(b)
          if (delta !== 0) return delta
          // Exact matches both score 0, and 0 * boost is still 0, so the
          // multiplier alone cannot separate a page from a post that matches
          // the query just as well. Break that tie toward the page: someone
          // typing an exact page name wants the destination.
          const isPage = (r: (typeof searchResults)[number]) =>
            r.item.category === 'page' ? 0 : 1
          return isPage(a) - isPage(b)
        })
        .map(result => result.item)
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      results = results.filter(item => item.category === categoryFilter)
    }

    // Apply sorting
    if (sortBy === 'title') {
      results = [...results].sort((a, b) => a.title.localeCompare(b.title))
    } else if (sortBy === 'category') {
      results = [...results].sort((a, b) =>
        a.category.localeCompare(b.category)
      )
    }
    // 'relevance' is already sorted by Fuse.js score

    return results
  }, [query, fuse, searchIndex, categoryFilter, sortBy])

  // Calculate category breakdown
  const categoryBreakdown = useMemo(() => {
    const breakdown = {
      blog: 0,
      project: 0,
      service: 0,
      page: 0,
    }
    allResults.forEach(item => {
      breakdown[item.category]++
    })
    return breakdown
  }, [allResults])

  const results = useMemo(() => {
    return allResults.slice(0, 8)
  }, [allResults])

  return {
    query,
    setQuery,
    results, // Limited to 8 for dropdown
    allResults, // All results for search page
    isSearching: query.trim().length > 0,
    categoryFilter,
    setCategoryFilter,
    sortBy,
    setSortBy,
    categoryBreakdown,
  }
}
